import { VALID_PLACEMENTS } from '../Header/Dropdown/Drawer/StyledHeaderDrawer';
import { themeMerge } from '../Utils';

import IThemeInterface, { breakPointSizes, edgeSize, zIndices } from './common';

const buttonDisabledOverride: Partial<IThemeInterface['button']['base']> = {
  background: 'stepBlue',
  border: { color: 'stepBlue' },
  font: { color: 'gray600' },
};

const theme: IThemeInterface = themeMerge({
  name: 'barclays',

  global: {
    breakPointSizes,

    colors: {
      darkBlue: '#00395d',
      digitalBlue: '#0076b6',
      digitalBlue10: '#c1deed',
      digitalBlue20: '#3d96c7',
      digitalBlue120: '#005fcc',
      green: '#008312',
      labelOrange: '#dc630a',
      lightBlue: '#e5f1f7',
      red: '#cc3333',
      red20: '#faeaea',
      red30: '#d86363',
      red120: '#9d063b',
      stepBlue: '#bfdded',
      gray700: '#333333',
      gray600: '#666666',
      gray500: '#8d8d8d',
      gray400: '#b2b2b2',
      gray300: '#d9d9d9',
      gray200: '#e5e5e5',
      gray100: '#f7f5f4',

      accent: 'darkBlue',
      black: '#000',
      error: 'red',
      focus: 'digitalBlue120',
      info: '',
      link: 'digitalBlue',
      primary: 'digitalBlue',
      secondary: 'lightBlue',
      success: 'green',
      tertiary: 'stepBlue',
      warning: 'labelOrange',
      white: '#fff',
    },

    edgeSize,

    elevation: {
      '1x': { color: 'rgba(0, 0, 0, 0.15)', shadow: '1px 2px 7px 1px' },
    },

    font: {
      family: {
        expert_sans: 'expert_sans',
        expert_sans_bold: 'expert_sans_bold',
        expert_sans_sm: 'expert_sans_sm',
        expert_sans_sm_bold: 'expert_sans_sm_bold',
      },
      size: {
        heroLarge: '3.6rem',
        heroMedium: '2.2rem',
        heroSmall: '1.8rem',
        body: '1.6rem',
        bodyLarge: '1.8rem',
        bodyMedium: '1.4rem',
        bodySmall: '1.2rem',
      },
    },

    outline: {
      color: 'focus',
      size: 'double',
      style: 'solid',
    },

    zIndex: zIndices,
  },

  banner: {
    info: {
      backgroundColor: 'gray100',
      border: {
        color: 'gray100',
      },
      font: { color: 'primary' },
      padding: { horizontal: '2x', vertical: '1.75x' },
      inlineLink: {
        color: 'primary',
        hoverColor: 'primary',
      },
      closeIcon: {
        color: 'primary'
      },
    },
    warning: {
      font: { color: 'white' },
      backgroundColor: 'primary',
      border: { color: 'primary' },
      inlineLink: {
        color: 'white',
        hoverColor: 'white',
      },
      closeIcon: {
        color: 'white'
      },
    },
    success: {
      font: { color: 'white' },
      backgroundColor: 'green',
      border: { color: 'green' },
      inlineLink: {
        color: 'white',
        hoverColor: 'white',
      },
      closeIcon: {
        color: 'white'
      },
    },
    error: {
      font: { color: 'white' },
      backgroundColor: 'error',
      border: { color: 'error' },
      inlineLink: {
        color: 'white',
        hoverColor: 'white',
      },
      closeIcon: {
        color: 'white'
      },
    },
  },

  button: {
    base: {
      background: 'primary',
      border: { color: 'primary', size: '2px'},
      borderRadius: '6.25em',
      shadow: 'none',
      font: {
        color: 'white',
        size: 'bodyLarge',
        letterSpacing: 'none',
        lineHeight: '1.334',
        textTransform: 'none',
        weight: '400'
      },
      outline: 'none',
      padding: { horizontal: '3x', vertical: '10px' },
      width: { min: '11.5em' },
      loadingIconInsideButton: true,
      spinner: {
        color: {
          animatedOutline: 'white',
          circleOutline: 'digitalBlue20',
        },
      },
      additionalStyles: ''
    },
    overrides: {
      primary: {
        state: {
          disabled: buttonDisabledOverride,
          hover: {
            background: 'accent',
            border: { color: 'accent', size: '2px' }
          },
          focus: {
            shadow: { shadow: '0 0 0 2px', color: 'white', inset: true },
          }
        },
      },

      secondary: {
        base: {
          background: 'white',
          font: { color: 'primary' },
          border: { color: 'primary', size: '2px' },
          padding: { horizontal: '3x', vertical: '10px' },
          spinner: {
            color: {
              animatedOutline: 'primary',
              circleOutline: 'digitalBlue10',
            },
          },
        },
        state: {
          disabled: buttonDisabledOverride,
          hover: {
            background: 'secondary',
            border: { color: 'accent' }
          },
          focus: {
            border: { color: 'digitalBlue120', size: '2px' },
            shadow: [
              { color: 'white', inset: true, shadow: '0 0 0 2px' },
              { color: 'primary', inset: true, shadow: '0 0 0 4px' },
            ],
          },
        },
      },

      destructive: {
        base: {
          background: 'error',
          font: { color: 'white' },
          border: { color: 'error', size: '2px' },
          spinner: {
            color: {
              animatedOutline: 'white',
              circleOutline: 'red30',
            },
          },
        },
        state: {
          disabled: {
            background: 'red20',
            border: { color: 'red20', size: '2px' },
            font: { color: 'black' },
          },
          hover: {
            background: 'red120',
            border: { color: 'red120', size: '2px' },
          },
          focus: {
            shadow: { shadow: '0 0 0 2px', color: 'white', inset: true },
            border: { color: 'primary', size: '2px' },
          }
        }
      },

      link: {
        base: {
          background: 'none',
          font: {
            color: 'link',
            textDecoration: 'underline',
            size: 'body',
            lineHeight: '1.5',
          },
          border: { size: 'none' },
          padding: 'none',
          width: { base: 'auto', min: 'auto'},
        },
        state: {
          hover: {
            font: { color: 'accent' },
          },
          focus: {
            font: { color: 'accent' },
            outline: { color: 'primary' },
          },
          disabled: { font: { color: 'gray500' } }
        }
      },

      text: {
        base: {
          background: 'none',
          font: {
            color: 'link',
            size: 'body',
            lineHeight: '1.5',
          },
          border: { size: 'double', color: 'transparent' },
        },
        state: {
          hover: {
            font: { color: 'accent' },
          },
          focus: {
            border: { color: 'primary' },
          },
          disabled: { font: { color: 'gray500' } }
        },
      },
    },
  },

  circleProgress: {
    backgroundPathColor: 'gray300',
    percentagePathColor: 'success',
    label: {
      percentage: {
        number: {
          size: 'heroLarge',
          lineHeight: '1.25',
          color: 'black',
          family: 'expert_sans',
        },
        sign: {
          size: 'body',
          weight: '600',
          verticalAlign: 'super',
        }
      },
      content: {
        family: 'expert_sans_sm',
        size: 'bodyMedium',
        color: 'black',
      },
    },
  },

  containerSize: {
    full: '100%',
    narrow: '51.2rem', // 512px
    'narrow-2': '70.4rem', // 704px
    wide: '89.6rem', // 896px
    max: '128rem', // 1280px
  },

  descriptionList: {
    term: {
      family: 'expert_sans',
      weight: '700',
      color: 'black',
      size: 'body',
      margin: { right: '3x' },
      lineHeight: '1.5',
    },

    detail: {
      family: 'expert_sans',
      weight: '400',
      color: 'black',
      size: 'body',
      lineHeight: '1.5',
    },

    variant: {
      large: {
        term: {
          weight: '700',
          lineHeight: '1.45',
          size: 'bodyMedium',
        },
        detail: {
          weight: '400',
          size: 'heroLarge',
          lineHeight: '1.23',
        },
      },

      small: {
        term: {
          weight: '700',
          lineHeight: '1.45',
          size: 'bodyMedium',
        },
        detail: {
          weight: '400',
          size: 'heroMedium',
          lineHeight: '1.3'
        },
      },
    }
  },

  form: {
    checkbox: {
      backgroundColor: 'white',
      border: { color: 'gray500' },
      icon: {
        size: '3x',
        color: 'white',
      },
      label: {
        font: { color: 'gray600', lineHeight: '1.45', size: 'bodyLarge' },
      },
      round: '6px',
      size: '3x',
      margin: { right: '1.5x' },

      state: {
        checked: {
          backgroundColor: 'primary',
          border: { color: 'primary' },
          label: {
            font: { color: 'primary' }
          },
        },

        disabled: {
          backgroundColor: 'gray100',
          border: { color: 'gray400' },
          label: { font: { color: 'gray500' }},
        },

        disabledChecked: {
          backgroundColor: 'gray100',
          border: { color: 'gray400' },
          icon: {
            color: 'gray500',
            size: '3x',
          },
        },

        error: {
          border: { color: 'error' },
          label: { font: { color: 'error' }},
        },

        focus: {
          focusPosition: 'labelAndIndicator',
          outline: { style: 'auto' },
        },

        hover: {},
      },
    },

    layout: {
      wrapper: {
        row: { margin: { bottom: '4x' } },
        stackRow: {
          margin: {
            base: { right: '0' },
            small: { right: '2x' },
          },
        },
      },
    },

    errorText: {
      checkboxAndRadioErrorIndention: true,
      font: {
        color: 'error',
        size: 'bodySmall',
        family: 'expert_sans_sm'
      },
      margin: { top: '1x' },
    },

    helperText: {
      font: {
        color: {
          default: 'gray600',
          disabled: 'gray500',
          readOnly: 'gray500',
        },
        size: 'bodySmall',
        weight: '400',
        lineHeight: '1.4',
        family: 'expert_sans_sm'
      },
      margin: { top: 'quadruple' },
    },

    input: {
      backgroundColor: 'white',
      border: { color: 'gray500', side: 'bottom', size: 'single' },
      iconColor: 'primary',
      font: { color: 'primary', lineHeight: '1.5' },
      padding: { horizontal: 'none', top: '0.75rem', bottom: '0.75rem' },
      placeholder: 'gray600',
      round: 'none',

      state: {
        hover: {},

        focus: {
          outline: { offset: 'none' },
        },

        error: {
          border: { color: 'error', side: 'bottom', size: 'single' },
          font: { color: 'error' },
        },

        disabled: {
          border: { color: 'transparent', size: 'single', side: 'bottom', },
          font: { color: 'gray500' }
        },

        readOnly: {
          border: { color: 'transparent', size: 'single', side: 'bottom', },
          font: { color: 'gray500' }
        },
      },
    },

    label: {
      font: {
        color: {
          default: 'gray700',
          disabled: 'gray500',
          readOnly: 'gray500',
        },
        family: 'expert_sans_sm',
        size: 'bodyMedium',
        weight: '400',
      },
      margin: { bottom: 'quadruple' },
    },

    legend: {
      font: {
        size: 'bodyMedium',
        color: 'gray700',
        family: 'expert_sans_sm',
      },
      margin: { bottom: '2x' },
    },

    radio: {
      button: {
        backgroundColor: 'white',
        checkedStyle: 'solid',
        checkedBgColor: 'primary',
        border: { color: 'gray500' },
        label: {
          font: { color: 'gray600', lineHeight: '1.35', size: 'bodyLarge' },
          margin: { left: '3.6rem' },
        },
        size: '3x',

        state: {
          checked: {
            shadow: { color: 'white', inset: true, shadow: '0 0 0 4px' },
            border: { color: 'primary' },
            label: { font: { color: 'primary' }},
          },
          disabled: {
            border: { color: 'gray400' },
            backgroundColor: 'gray100',
            label: { font: { color: 'gray500' }},
          },
          disabledChecked: {
            backgroundColor: 'gray400',
            shadow: { color: 'gray100', inset: true, shadow: '0 0 0 4px' },
            border: { color: 'gray400' },
          },
          error: {
            border: { color: 'error' },
            label: { font: { color: 'error' } },
          },
          focus: {
            focusPosition: 'labelAndIndicator',
          },
          hover: {
            border: { color: 'gray600' },
          },
        },
      },

      card: {
        backgroundColor: 'white',
        border: { size: '0.25px', color: 'gray100' },
        round: 'quadruple',
        shadow: '1x',

        state: {
          checked: {
            backgroundColor: 'secondary',
            border: { size: 'double', color: 'primary' },
          },
          focus: {
            border: { color: 'transparent' },
            outline: { offset: '5px', style: 'auto' },
          },
          hover: {
            backgroundColor: 'secondary',
            border: { size: '0.25px', color: 'secondary' },
          },
          disabled: {
            backgroundColor: 'gray100',
            border: { size: 'single', color: 'gray100' },
            shadow: 'none',
          },

          error: {
            text: {
              margin: { top: '3x' }
            }
          },
        },
      },

      badge: {
        backgroundColor: 'white',
        border: { color: 'gray500', size: '0.1rem' },
        padding: { vertical: '1.5x', horizontal: '2.5x' },
        round: '0.5rem',
        width: { min: '4.8125em' },
        font: { color: 'gray700' },

        /* Only real functional shadows here are on the hover states. The other shadows
           are so the hover states don't override the border color when we don't want to */
        state: {
          checked: {
            backgroundColor: 'secondary',
            border: { color: 'primary' },
            font: { color: 'primary' },
            shadow: { inset: true, color: 'primary', shadow: '0 0 0 1px' }
          },
          checkedHover: {
            border: { color: 'primary' },
            backgroundColor: 'digitalBlue10',
            shadow: { inset: true, color: 'primary', shadow: '0 0 0 1px' },
          },
          disabled: {
            border: { color: 'gray200' },
            font: { color: 'gray500' },
            shadow: { inset: true, color: 'gray200', shadow: '0 0 0 1px' },
          },
          disabledChecked: {
            backgroundColor: 'gray200',
            border: { color: 'gray200' },
            font: { color: 'gray500' },
            shadow: { inset: true, color: 'gray200', shadow: '0 0 0 1px' },
          },
          error: {
            border: { color: 'error' },
            font: { color: 'error' },
            shadow: { inset: true, color: 'error', shadow: '0 0 0 1px' },
          },
          focus: {
            outline: { offset: '3px', style: 'auto' }
          },
          hover: {
            shadow: { inset: true, color: 'gray500', shadow: '0 0 0 1px' },
          },
        },
      },

      toggle: {
        label: {
          color: 'gray600',
          size: 'bodyLarge',
        },

        button: {
          height: '3x',
          width: '5x',
          margin: { right: '1.5x' },
          padding: { vertical: 'single', horizontal: 'single' },
          border: 'none',
          backgroundColor: 'gray500',
        },

        slidingIndicator: {
          size: '2.75x',
          color: 'white',
        },

        state: {
          checked: {
            label: { color: 'primary' },
            button: {
              border: 'none',
              backgroundColor: 'primary',
            },
            slidingIndicator: {
              color: 'white',
            },
          },

          disabled: {
            label: { color: 'gray500' },
            button: {
              border: 'none',
              backgroundColor: 'gray400',
            },
            slidingIndicator: {
              color: 'gray100',
            },
          },

          disabledChecked: {
            label: { color: 'gray500' },
            button: {
              border: 'none',
              backgroundColor: 'gray400',
            },
            slidingIndicator: {
              color: 'gray100',
            },
          },

          focus: {
            label: { outline: { offset: 'double', }},
          },

          hover: {
            button: { border: 'none' },
          },
        },
      },
    },

    select: {
      icon: {
        size: '3x',
        color: 'gray500',
        errorColor: 'error',
      },
    },

    slider: {
      outline: { color: 'focus' },
      thumb: {
        color: { default: 'primary', disabled: 'gray500' },
        height: 36,
        width: 36,
      },
      track: {
        color: {
          base: { default: 'gray200', disabled: 'gray100' },
          progress: { default: 'digitalBlue20', disabled: 'gray300' }
        },
        thickness: 8,
        borderRadius: '4px',
      },
      unitLabels: {
        color: { default: 'black', disabled: 'gray700' },
        margin: { top: '2x' },
      }
    },

    textarea: {
      backgroundColor: 'white',
      border: { color: 'gray600' },
      font: { color: 'primary' },
      padding: { top: '1.5x', bottom: '1.5x', horizontal: '2x' },
      round: '1x',

      state: {
        hover: {
          border: { color: 'gray600' },
        },

        focus: {
          outline: { offset: 'none' },
        },

        error: {
          border: { color: 'error' },
          font: { color: 'error' },
        },

        disabled: {
          border: { color: 'transparent' },
          font: { color: 'gray500' },
        },
      },
    },
  },

  heading: {
    default: {
      color: 'accent',
      family: 'expert_sans',
      lineHeight: '1.275',
      weight: '400',
    },

    level: {
      h1: {
        lineHeight: '1.225',
        size: { base: 'heroMedium', medium: 'heroLarge' },
      },

      h2: {
        size: { base: 'heroSmall', medium: 'heroMedium' },
      },

      h3: { size: 'heroSmall' },

      h4: {/* level specific styles */ },
      h5: {/* level specific styles */ },
      h6: {/* level specific styles */ },
    },
  },

  layout: {
    header: {
      border: { size: '1px', color: 'gray400', side: 'bottom'},
      padding: {
        base: { horizontal: '2.5x' },
        large: { horizontal: '10x' },
        xlarge: { horizontal: '5%' },
      },
      height: { base: '6.4rem', small: '8rem' },

      dropdown: {
        shadow: '1x',
        width: { base: '100%', small: '37.5rem' },
        backgroundColor: 'white',
        icon: {
          desktop: {
            size: '2.75x',
            color: 'link',
            type: 'arrow-bottom',
          },
          mobile: {
            open: {
              type: 'menu',
              color: 'primary',
              size: '3x'
            },
            close: {
              size: '3x',
              color: 'primary',
              type: 'close'
            }
          }
        },

        button: {
          font: {
            color: 'link',
            size: 'body',
            lineHeight: '1.5',
          },

          state: {
            hover: { font: { color: 'accent' }}
          }
        },

        link: {
          divider: {
            placement: VALID_PLACEMENTS.allChildren,
            height: '1px',
            backgroundColor: 'gray300',
            width: { base: '100%', small: 'calc(100% - 4.8rem)' }
          },
          border: { size: 'double', color: 'transparent' },
          padding: { vertical: '2.25x', horizontal: '3x' },
          font: {
            color: 'link',
            lineHeight: '1.35',
            size: 'bodyLarge',
          },

          state: {
            hover: {
              font: { color: 'accent' },
              backgroundColor: 'secondary'
            },
            focus: {
              border: { size: 'double', color: 'focus' },
            }
          }
        },
      },

      link: {
        font: {
          color: 'link',
          lineHeight: '1.5',
          size: 'body',
        },
        margin: {
          right: '4x',
        },
        state: {
          hover: { font: { color: 'accent' }},
        },
      },
    },
  },

  link: {
    inline: {
      state: {
        hover: { color: '' },
      },
    },
  },

  list: {
    margin: { left: '2x' },

    listItem: {
      common: {
        margin: { bottom: '1x' },
        font: {
          family: 'expert_sans',
          lineHeight: '1.5',
          size: 'body',
          color: 'black',
          weight: '400'
        },
      },

      bullet: {
        backgroundColor: 'black',
        listStyleSpacing: '2.6rem',
        size: '0.75x',
      },

      icon: {
        listStyleSpacing: '1.5x',
        size: '2.5x',
        type: 'done',
        color: 'black',
      },

      number: {
        listStyleSpacing: '3.4rem',
        font: {
          weight: '400',
          color: 'black',
        },
      },
    },
  },

  modal: {
    overlay: {
      backgroundColor: 'rgba(0, 0, 0, .7)',
      padding: {
        base: { top: '3x' },
        medium: { top: '11x' },
      },
    },
    card: {
      backgroundColor: 'white',
      border: { color: 'gray100', size: '.25px' },
      padding: {
        base: { vertical: '4x', horizontal: '3x' },
        medium: { horizontal: '4x' },
      },
      round: '1x',
      shadow: '1x'
    },
    closeIcon: {
      type: 'alert-close',
      size: '1.75x',
      color: 'primary',
      padding: '2.5x'
    },
  },

  spinner: {
    size: '5em',
    iconStrokeWidth: 2,
    withOutline: true,
    color: {
      animatedOutline: 'primary',
      circleOutline: 'secondary',
    },
  },

  table: {
    border: 'none',

    tableRow: {
      height: '1px',
    },

    tableHeader: {
      rowDivider: 'gray200',
      padding: {
        base: { vertical: '1x', horizontal: '1.5x' },
        small: { vertical: '1x', horizontal: '2x' },
      },
      font: {
        size: 'bodyMedium',
        lineHeight: '1.125',
      },
    },

    tableBody: {
      rowDivider: 'gray200',
      padding: {
        base: { vertical: 'quadruple', horizontal: '1.5x' },
        small: { vertical: '2.5x', horizontal: '2x' },
      },
      font: {
        size: 'body',
        lineHeight: '1.25',
      },
    },
  },

  tooltip: {
    activationType: 'automatic',
    pane: {
      background: 'white',
      padding: { horizontal: '1.5x', vertical: '2x' },
      shadow: '1x',
      round: '1x',
      border: { color: 'primary' },
      verticalOffset: '20%',
      position: 'top',

      font: {
        size: 'bodyMedium',
        color: 'gray700',
        family: 'expert_sans_sm'
      },
    },

    icon: {
      size: '1.6rem',
      color: 'primary',
      hover: 'darkBlue',
      margin: { left: '1x' },
    },
  },

  typography: {
    text: {
      body: {
        default: {
          lineHeight: '1.5',
          family: 'expert_sans',
        },
        large: { lineHeight: '1.35' },
        medium: { lineHeight: '1.75' },
        small: { lineHeight: '2' },
      },
    },

    pill: {
      default: {
        padding: { vertical: '4px', horizontal: '2x' },
        font: {
          transform: 'uppercase',
          color: 'white',
          family: 'expert_sans',
          size: 'body',
          lineHeight: '1.25',
          weight: '700',
        },
        round: '100px',
      },
      neutral: { backgroundColor: 'gray700' },
      success: { backgroundColor: 'success' },
      warning: {
        backgroundColor: 'warning',
        font: { color: 'black' }
      },
      error: { backgroundColor: 'error' },
      info: { backgroundColor: 'info' },
    },
  },
  datePicker: {
    input: {
      // tslint:disable:max-line-length
      icon: (color: string) => `"data:image/svg+xml;charset=utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16'%3E%3Cpath fill='${encodeURIComponent(color)}' d='M15 2h-2V0h-2v2H9V0H7v2H5V0H3v2H1c-.553 0-1 .447-1 1v12c0 .553.447 1 1 1h14c.553 0 1-.447 1-1V3c0-.553-.447-1-1-1zm-1 12H2V6h12v8z'/%3E%3C/svg%3E"`,
    },
    focusRing: {
      outline: {
        color: 'focus',
        size: 'double',
        style: 'solid',
      }
    }
  }
});

export default theme;
